import { useNavigate } from 'react-router-dom'

import {
  getCookieByName,
  getUtmParameters,
  getValueOrZero,
} from 'src/Constants/functions'
import { useCreateCampaignEvents } from 'src/Hooks/Analytics/useCreateCampaignEvents'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { getAccessToken } from 'src/Redux/auth-process/credentialsSlice/selectors'
import { setIsFirstCampaign } from 'src/Redux/auth-process/userSlice'
import { useCreateNewTiktokCampaignMutation } from 'src/Redux/createNewCampaignApi'
import {
  CreateSpotifyCampaignResponse,
  useCreatePreReleaseSpotifyCampaignMutation,
  useCreateSpotifyCampaignMutation,
  useUpdateSpotifyCampaignGenresMutation,
  useUpdateSpotifyCampaignLanguagesMutation,
} from 'src/Redux/spotify-campaignApi'
import { ANY_LANGUAGE_ID } from 'src/Constants/constants'
import { sendSourceBusterEvent } from 'src/Redux/sourcebuster-process/api-actions'
import {
  setTiktokTrackToInitialState,
  setTiktokTracksToInitialState,
} from 'src/Redux/tiktokTrack-process'
import {
  campaignsList,
  createCampaign,
  scheduleCampaign,
  scheduleCampaignTikTok,
} from 'src/Router/routes'
import {
  CampaignPlatformTypeEnum,
  CampaignStatusEnum,
  CampaignTypeEnum,
  TiktokCampaignStatus,
} from 'src/Types/campaigns'
import { ScheduleSteps } from 'src/Types/ui'
import {
  SpotifyTrack,
  TikTokTrack,
} from 'src/Pages/Artist/CreateCampaignPage/AuthPages/SelectedTrackAuth/useSelectedTrackAuth'
import { getValueOrEmptyString } from 'src/Helpers/layoutHelpers'

import { getSpotifyArtistUrl } from './Components/SearchArtistSection/helpers'

export interface PreReleaseTrackDetails {
  artistName: string
  artistSpotifyId: string
  trackName: string
}

interface ICreateNewCampaign {
  createNewSpotifyCampaign: (track: SpotifyTrack) => void
  createNewPreReleaseSpotifyCampaign: (
    trackDetails: PreReleaseTrackDetails,
  ) => Promise<CreateSpotifyCampaignResponse>
  createNewTiktokCampaign: (track: TikTokTrack) => void
  loading: boolean
}

export const useCreateNewCampaign = (): ICreateNewCampaign => {
  const token = useAppSelector(getAccessToken)

  const [createSpotifyCampaign, { isLoading: isSpotifyLoading }] =
    useCreateSpotifyCampaignMutation()
  const [
    createPreReleaseSpotifyCampaign,
    { isLoading: isPreReleaseSpotifyLoading },
  ] = useCreatePreReleaseSpotifyCampaignMutation()
  const [updateSpotifyCampaignGenres] = useUpdateSpotifyCampaignGenresMutation()
  const [updateSpotifyCampaignLanguages] =
    useUpdateSpotifyCampaignLanguagesMutation()
  const [createTiktokCampaign, { isLoading: isTiktokLoading }] =
    useCreateNewTiktokCampaignMutation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const {
    createCampaignEventHandler,
    createdTikTokCampaignEventHandler,
    createdSpotifyCampaignEventHandler,
  } = useCreateCampaignEvents()

  const sendSourceBusterData = (
    id: number,
    campaignPlatform: CampaignPlatformTypeEnum,
    campaignType: CampaignTypeEnum,
  ): void => {
    const utmObj = getUtmParameters(getCookieByName('soundCampaignUtm'))
    const eventName =
      campaignType === CampaignTypeEnum.FIRST
        ? 'create_first_campaign'
        : 'create_basic_campaign'

    void dispatch(
      sendSourceBusterEvent({
        token,
        campaignPlatform,
        campaignId: id,
        eventName,
        gclid: utmObj.gclid || '',
      }),
    )
  }

  const applyCreatedSpotifyCampaignSideEffects = (
    response: CreateSpotifyCampaignResponse,
  ): void => {
    void updateSpotifyCampaignGenres({
      id: String(response.id),
      genreIds: [],
    })
    void updateSpotifyCampaignLanguages({
      id: String(response.id),
      languageIds: [ANY_LANGUAGE_ID],
    })

    createCampaignEventHandler({ campaignId: response.id })

    createdSpotifyCampaignEventHandler({
      campaignId: response.id,
      spotifyTrackId: response.track.id,
    })

    sendSourceBusterData(
      response.id,
      CampaignPlatformTypeEnum.SPOTIFY,
      response.type,
    )
  }

  const createNewSpotifyCampaign = async (
    spotifyTrack: SpotifyTrack,
  ): Promise<void> => {
    dispatch(setIsFirstCampaign(false))
    if (!spotifyTrack) {
      return
    }
    await createSpotifyCampaign({
      trackName: getValueOrEmptyString(spotifyTrack?.trackName),
      trackImage: getValueOrEmptyString(spotifyTrack?.trackCoverUrl),
      trackLength: getValueOrZero(spotifyTrack?.trackDurationInMs),
      artistName: getValueOrEmptyString(spotifyTrack?.artistName),
      artistSpotifyId: getValueOrEmptyString(spotifyTrack?.artistSpotifyId),
      trackSpotifyId: getValueOrEmptyString(spotifyTrack?.trackId),
      trackUrl: `https://api.spotify.com/v1/tracks/${spotifyTrack.trackId}`,
      artistUrl: `https://open.spotify.com/artist/${spotifyTrack.artistSpotifyId}`,
    })
      .unwrap()
      .then((response) => {
        applyCreatedSpotifyCampaignSideEffects(response)

        if (response.status === CampaignStatusEnum.APPROVED) {
          navigate(
            `${campaignsList}${scheduleCampaign}/${response.id}?step=${ScheduleSteps.SETTINGS}`,
          )
        } else {
          navigate(
            `${campaignsList}${createCampaign}/${CampaignPlatformTypeEnum.SPOTIFY}?successCreatedCampaign=true`,
          )
        }
        return response
      })
  }

  const createNewPreReleaseSpotifyCampaign = async ({
    artistName,
    artistSpotifyId,
    trackName,
  }: PreReleaseTrackDetails): Promise<CreateSpotifyCampaignResponse> => {
    dispatch(setIsFirstCampaign(false))

    const response = await createPreReleaseSpotifyCampaign({
      artistName,
      artistSpotifyId,
      trackName,
      artistUrl: getSpotifyArtistUrl(artistSpotifyId),
    }).unwrap()

    applyCreatedSpotifyCampaignSideEffects(response)

    return response
  }

  const createNewTiktokCampaign = async (
    tikTokTrack: TikTokTrack,
  ): Promise<void> => {
    await createTiktokCampaign({
      initialVideoCount: getValueOrZero(tikTokTrack.initialVideoCount),
      trackAuthorName: getValueOrEmptyString(tikTokTrack.artistName),
      trackCoverUrl: getValueOrEmptyString(tikTokTrack.trackCoverUrl),
      trackDuration: getValueOrZero(tikTokTrack.trackDurationInMs),
      trackPlayUrl: getValueOrEmptyString(tikTokTrack.trackPreviewUrl),
      trackTiktokId: getValueOrEmptyString(tikTokTrack.trackId),
      trackTitle: getValueOrEmptyString(tikTokTrack.trackName),
      trackUrl: getValueOrEmptyString(tikTokTrack.trackUrl),
    })
      .unwrap()
      .then((response) => {
        createCampaignEventHandler({ campaignId: response.id })
        createdTikTokCampaignEventHandler({
          campaignId: response.id,
          trackTiktokId: response.trackTiktokId,
        })

        sendSourceBusterData(
          Number(response.id),
          CampaignPlatformTypeEnum.TIKTOK,
          response.type,
        )

        dispatch(setTiktokTracksToInitialState())
        dispatch(setTiktokTrackToInitialState())
        if (response.status === TiktokCampaignStatus.PENDING) {
          navigate(
            `${campaignsList}${scheduleCampaignTikTok}/${response.id}?step=${ScheduleSteps.SETTINGS}`,
          )
        } else {
          navigate(
            `${campaignsList}${createCampaign}/${CampaignPlatformTypeEnum.TIKTOK}?successCreatedCampaign=true`,
          )
        }

        return response
      })
  }
  return {
    createNewSpotifyCampaign,
    createNewPreReleaseSpotifyCampaign,
    createNewTiktokCampaign,
    loading: isSpotifyLoading || isPreReleaseSpotifyLoading || isTiktokLoading,
  }
}
